import BoxContainer from "@standard/components/BoxContainer/BoxContainer";
import { useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import DataForm from "@standard/components/DataForm/DataForm";
import { DatasourceAPI, OrderAPI } from "api";
import {
  AlertService,
  AuthService,
  DateService,
  ErrorService,
  FormService,
  NumberService,
} from "@standard/services";
import { useForm } from "react-hook-form";
import { JOB_STATUS, JobStatusComponent } from "data/jobStatus";
import Modal from "@standard/components/Modal";
import AlertBar from "@standard/components/AlertBar";
import JobHistories from "./JobHistories";
import JobDocuments from "./JobDocuments";
import { USER_ROLE } from "data/constants";
import JobAlertReject from "./JobAlertReject";
import { TableForm } from "@standard/components/TableForm";
import DocumentTable from "pages/Master/DocumentTemplate/DocumentTable";
import JobFormDocumentTemplateTable from "./JobFormDocumentTemplateTable";
import mongoose from "mongoose";

export default ({ history, url }) => {
  const { id, action } = useParams();
  const [errors, setErrors] = useState([]);
  const [disabled, setDisabled] = useState(action === "view");
  const [isLoading, setIsLoading] = useState(true);
  const [datasource, setDatasource] = useState({
    user: [],
    documentTemplate: [],
  });
  const { _id: currentUserId } = AuthService.getCurrentUser();

  const [jobHistories, setJobHistories] = useState([]);
  const [jobAuthenHistories, setJobAuthenHistories] = useState([]);
  const [documents, setDocuments] = useState([]);

  const defaultValues = {
    id: id,
    name: "",
    customerName: "",
    documentTemplateId: null,
    userId: null,
    newStatus: "",
    status: JOB_STATUS.WAITING,
    serviceUsers: "",
    remark: "",
    assets: [],
    documentsCount: 0,
  };

  const reactHookForm = useForm({ defaultValues });

  useEffect(() => {
    if (id !== undefined) retrieveData(id);
  }, []);

  const retrieveData = (id) => {
    FormService.getRequest(url, id).then((result) => {
      const tables = [
        { name: "user", ref1: USER_ROLE.SALE, value: result.userId },
        {
          name: "documentTemplate",
          ref1: result.customerId,
          value: result.documentTemplateId,
        },
      ];

      DatasourceAPI.getActiveDatasource(tables).then((datasourceResult) => {
        setDatasource((prev) => {
          return { ...prev, ...datasourceResult };
        });

        if (
          !result.userId &&
          datasourceResult.user.find((f) => f.key === currentUserId)
        )
          result.userId = currentUserId;

        reactHookForm.reset(result);

        setJobHistories(result.jobHistories);
        if (result.documents) {
          setDocuments(result.documents);
        }

        setJobAuthenHistories(result.jobAuthenHistories);
        setIsLoading(false);
        setDisabled(
          [JOB_STATUS.COMPLETE, JOB_STATUS.AUTHENING].includes(result.status) ||
            action === "view"
        );
      });
    });
  };

  const saveData = (newData) => {
    setIsLoading(true);

    const params = { ...newData, id: id };
    params.status = params.newStatus;
    params.documents = documents.map((item) => {
      const _id = item.key ?? new mongoose.Types.ObjectId();
      params[`exampleAssets[${_id}]`] = item.values.exampleAssets;

      return {
        _id: _id,
        name: item.values.name,
        documentTemplateDocumentId:
          item.values.documentTemplateDocumentId ?? null,
        deletedExampleAssets:
          item.values.exampleFiles
            ?.filter(
              (asset) => asset.isDelete === true && asset.downloadable !== true
            )
            .map((asset) => asset._id) ?? [],
        copyExampleAssets:
          item.values.exampleFiles
            ?.filter(
              (asset) => asset.isDelete !== true && asset.downloadable === true
            )
            .map((asset) => asset._id) ?? [],
      };
    });

    FormService.submitRequestWithFile(url, params)
      .then(() => {
        setIsLoading(false);
        setErrors([]);
        history.push(`/${url}`);
      })
      .catch((err) => {
        console.log("Catch error", err);
        setIsLoading(false);
        setErrors(err);
      });
  };

  const onSubmit = (newData) => {
    let confirmMessage = "Do you want to save data";
    AlertService.confirm(confirmMessage).then((res) => {
      if (res === true) saveData(newData);
    });
  };

  const tabs = [
    {
      name: "info",
      label: "Info",
      fields: [
        { name: "projectCode", label: "Project Code", type: "label", col: 6 },
        { name: "name", label: "Job Name", type: "label", col: 6 },
        { name: "customerName", label: "Customer Name", type: "label", col: 6 },
        { name: "machine", label: "Machine", type: "label", col: 6 },
        { name: "workDateText", label: "Work Date", type: "label", col: 12 },
        {
          name: "documentTemplateId",
          label: "Document Template",
          type: "ddl",
          datasource: datasource.documentTemplate,
          required: true,
          disabled: disabled || reactHookForm.watch("documentsCount") > 0,
        },
        {
          type: "custom",
          control: (
            <JobFormDocumentTemplateTable
              key="documents"
              values={documents}
              onChange={(newDocuments) => {
                setDocuments(newDocuments);
              }}
              onFileChange={(index, field) => {
                setDocuments((prev) => {
                  return prev.map((item, itemIndex) => {
                    if (itemIndex === index)
                      item.values[field.name] = field.value;
                    return item;
                  });
                });
              }}
              disabled={disabled}
            />
          ),
        },
        {
          name: "userId",
          label: "Owner",
          type: "ddl",
          datasource: datasource.user,
          required: true,
          disabled: disabled,
        },
        {
          name: "serviceUsers",
          label: "Services",
          type: "label",
        },
        {
          name: "remark",
          label: "Remark",
          type: "textarea",
          disabled: disabled,
        },
        {
          name: "assets",
          label: "Attach Files",
          type: "file",
          multiple: true,
          disabled: disabled,
        },
        {
          name: "assetsPreview",
          type: "filePreview",
          disabled: disabled,
        },
      ],
    },
    {
      name: "document",
      label: "Documents",
      fields: [
        {
          type: "custom",
          control: (
            <JobDocuments
              key="document"
              values={documents}
              disabled={true}
              onChange={(newDocument) => setDocuments(newDocument)}
              enableOptions={!disabled}
            />
          ),
        },
      ],
    },
    {
      name: "history",
      label: "History",
      fields: [
        {
          type: "custom",
          control: <JobHistories key="jobHistory" values={jobHistories} />,
        },
      ],
    },
    {
      name: "authenhistory",
      label: "Authen History",
      fields: [
        {
          type: "custom",
          control: (
            <JobHistories key="jobAuthenHistory" values={jobAuthenHistories} />
          ),
        },
      ],
    },
  ];

  useEffect(() => {
    if (isLoading === true) return;

    const documentTemplateId = reactHookForm.watch("documentTemplateId");
    if (!documentTemplateId) return;

    setIsLoading(true);
    const tables = [
      { name: "documentTemplateDocuments", ref1: documentTemplateId },
    ];
    DatasourceAPI.getActiveDatasource(tables).then((res) => {
      setDocuments(
        res.documentTemplateDocuments.map((item) => {
          return {
            key: null,
            values: { ...item.info, documentTemplateDocumentId: item.key },
          };
        })
      );
      setIsLoading(false);
    });
  }, [reactHookForm.watch("documentTemplateId")]);

  const fieldErrors = ErrorService.convertToArray(
    reactHookForm.formState.errors
  );

  let buttons = [];

  const leftPanel = (
    <div>
      <JobStatusComponent value={reactHookForm.getValues("status")} />
    </div>
  );

  if (disabled === false) {
    const status = reactHookForm.getValues("status");
    if (status === JOB_STATUS.WAITING) {
      buttons = [
        <button
          key="processing"
          className="btn btn-primary mr-2"
          onClick={() =>
            reactHookForm.setValue("newStatus", JOB_STATUS.PROCESSING)
          }
        >
          บันทึกรับงาน
        </button>,
      ];
    } else if (
      status === JOB_STATUS.PROCESSING ||
      status === JOB_STATUS.REJECT
    ) {
      buttons = [
        <button key="processing" className="btn btn-primary mr-2">
          บันทึกข้อมูล
        </button>,
      ];
    } else if (status === JOB_STATUS.DONE) {
      buttons = [
        <button
          key="processing"
          className="btn btn-primary mr-2"
          onClick={() => reactHookForm.setValue("newStatus", "")}
        >
          บันทึกข้อมูล
        </button>,
        <button
          key="done"
          className="btn btn-danger mr-2"
          onClick={() =>
            reactHookForm.setValue("newStatus", JOB_STATUS.COMPLETE)
          }
        >
          บันทึกปิดงาน
        </button>,
        <button
          key="renew"
          className="btn btn-warning mr-2"
          onClick={() =>
            reactHookForm.setValue("newStatus", JOB_STATUS.PROCESSING)
          }
        >
          บันทึกเปิดงานใหม่
        </button>,
      ];
    }
  }

  return (
    <BoxContainer loading={isLoading} errors={errors.concat(fieldErrors)}>
      <>
        <JobAlertReject value={reactHookForm.getValues("rejectInfo")} />
        <DataForm
          leftPanel={leftPanel}
          refForm={reactHookForm}
          tabs={tabs}
          buttons={buttons}
          url={url}
          enableSave={false}
          enableTabs={true}
          onSubmit={onSubmit}
        ></DataForm>
      </>
    </BoxContainer>
  );
};
